/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { Redirect, useRouter } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { WaitlistEntryReason } from '../../../common/analytics/types';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { BENCHMARK_ROUTE_PATH, isBenchmarkRunRouteName } from '../../../routes';
import { forceBenchmarkWaitlistViewAtom } from '../../state';

// Both pages are split into their own chunks and only fetched once this
// route decides to render them.
const BenchmarkPage = lazy(() =>
	import(/* webpackChunkName: "@atlassian/twg-microsite_benchmark-page" */ '../benchmark').then(
		(module) => ({ default: module.BenchmarkPage }),
	),
);

const BenchmarkWaitlistPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_benchmark-waitlist-page" */ '../benchmark-waitlist'
	).then((module) => ({ default: module.BenchmarkWaitlistPage })),
);

const LazyBenchmarkPage = (): JSX.Element => (
	<Suspense fallback={null}>
		<BenchmarkPage />
	</Suspense>
);

type LazyBenchmarkWaitlistPageProps = {
	isAuthed: boolean;
	entryReason: WaitlistEntryReason;
	/** The signed-in visitor's account email, which prefills the form. */
	email: string;
};

const LazyBenchmarkWaitlistPage = ({
	isAuthed,
	entryReason,
	email,
}: LazyBenchmarkWaitlistPageProps): JSX.Element => (
	<Suspense fallback={null}>
		<BenchmarkWaitlistPage
			authState={isAuthed ? 'loggedIn' : 'loggedOut'}
			entryReason={entryReason}
			defaultEmail={isAuthed ? email : ''}
		/>
	</Suspense>
);

/**
 * Route component for `/benchmark` and for a run, its review and its report.
 *
 * All four routes must render this one component. Starting a run navigates from `/benchmark`
 * to the run's URL while the start request is still in flight; a different component type on
 * the run routes would remount the benchmark page there, losing the run being started, and the
 * new page would ask for a run the service has not stored yet and report that it could not
 * update it.
 *
 * Signed-in users with the cloud benchmark get the benchmark. Everyone else gets the public
 * waitlist at `/benchmark`, and is sent there from a run, which means nothing to them. `isAuthed`
 * is final here because `UserProvider` only mounts the router once the profile fetch settles. The
 * Debug Panel can force the waitlist view at `/benchmark` to preview it.
 */
export const BenchmarkRoute = (): JSX.Element => {
	const { isAuthed, identity } = useAtlassianUserContext();
	const forceWaitlistView = useAtomValue(forceBenchmarkWaitlistViewAtom);
	const [{ route }] = useRouter();
	const onRun = isBenchmarkRunRouteName(route?.name);

	if (forceWaitlistView && !onRun) {
		return (
			<LazyBenchmarkWaitlistPage
				isAuthed={isAuthed}
				entryReason="debugOverride"
				email={identity.email}
			/>
		);
	}

	if (fg('graphyte-twg-cloud-benchmark') && isAuthed) {
		return <LazyBenchmarkPage />;
	}

	return onRun ? (
		<Redirect to={BENCHMARK_ROUTE_PATH} />
	) : (
		<LazyBenchmarkWaitlistPage
			isAuthed={isAuthed}
			entryReason={isAuthed ? 'publicLoggedInWithoutCloudBenchmark' : 'publicLoggedOut'}
			email={identity.email}
		/>
	);
};
